import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Input, Select, Space, Tag, Modal, Form, message, Row, Col,
  Tooltip, Popconfirm, Typography, Button, Empty, Switch, InputNumber,
} from 'antd';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  BookOutlined, CloseCircleOutlined, FilterOutlined, WarningOutlined,
} from '@ant-design/icons';
import { glossaryAPI } from '../services/api';
import { usePermission } from '../contexts/PermissionContext';
import { useFillHeight } from '../hooks/useViewportFit';
import { brand, gray, radius } from '../theme/tokens';
import { EmptyState } from '../components/common';

interface GlossaryTerm {
  id: number;
  term: string;
  term_en: string;
  category: string;
  definition: string;
  detail: string;
  aliases: string;
  screens: string;
  needs_review: boolean;
  sort_order: number | null;
  is_active: boolean;
}

/* 분류별 색상 — 시험 흐름(설계→일정→물질→상태) 순으로 난색→한색 */
const CATEGORY_COLORS: Record<string, string> = {
  '시험설계': '#6366f1',
  '시험일정': '#0ea5e9',
  '시험물질': '#10b981',
  '진행상태': '#f59e0b',
  '조직·주체': '#f472b6',
  '문서·성적서': '#8b5cf6',
  '외부연계': '#14b8a6',
  '시스템·운영': '#64748b',
};
const catColor = (c: string) => CATEGORY_COLORS[c] || '#6366f1';

const GlossaryPage: React.FC = () => {
  const [items, setItems] = useState<GlossaryTerm[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [reviewOnly, setReviewOnly] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<GlossaryTerm | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const { hasPermission } = usePermission();
  /* 문서 세로스크롤 0 정책 — 목록이 길어도 카드 안에서만 스크롤되게 실측 높이를 준다 */
  const { ref: fillRef, height: fillHeight } = useFillHeight(280);
  const canWrite = hasPermission('glossary', 'WRITE');
  const canDelete = hasPermission('glossary', 'DELETE');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await glossaryAPI.list({
        search: search.trim() || undefined,
        category: category || undefined,
        needs_review: reviewOnly ? 'y' : undefined,
      });
      setItems(data.items || []);
      // 분류 목록은 필터가 걸린 결과에 좌우되지 않게 전체 기준으로만 갱신
      if (!search.trim() && !category && !reviewOnly) setCategories(data.categories || []);
    } catch {
      message.error('용어사전을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [search, category, reviewOnly]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    // 최초 1회 전체 분류 확보
    glossaryAPI.list().then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, []);

  /** 분류별로 묶어 사전처럼 보여준다 */
  const grouped = useMemo(() => {
    const map = new Map<string, GlossaryTerm[]>();
    items.forEach(t => {
      const k = t.category || '미분류';
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(t);
    });
    return Array.from(map.entries());
  }, [items]);

  const reviewCount = useMemo(() => items.filter(t => t.needs_review).length, [items]);
  const activeFilters = [category, reviewOnly ? 'r' : ''].filter(Boolean).length;

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ needs_review: false, sort_order: 999, is_active: true });
    setFormOpen(true);
  };

  const openEdit = (t: GlossaryTerm) => {
    setEditing(t);
    form.setFieldsValue(t);
    setFormOpen(true);
  };

  const submitForm = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await glossaryAPI.update(editing.id, values);
        message.success('수정되었습니다.');
      } else {
        await glossaryAPI.create(values);
        message.success('등록되었습니다.');
      }
      setFormOpen(false);
      fetchData();
    } catch (err: any) {
      message.error(err.response?.data?.detail || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await glossaryAPI.delete(id);
      message.success('삭제되었습니다.');
      fetchData();
    } catch (err: any) {
      message.error(err.response?.data?.detail || '삭제에 실패했습니다.');
    }
  };

  return (
    <>
      <div ref={fillRef} style={{ height: fillHeight }}>
      <Card
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        styles={{ body: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <BookOutlined style={{ color: brand.primary, fontSize: 18 }} />
            <div>
              <Typography.Title level={5} style={{ margin: 0 }}>용어사전</Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                총 {items.length}개 용어
                {activeFilters > 0 && ' (필터 적용)'}
                {reviewCount > 0 && ` · 뜻 확정 필요 ${reviewCount}건`}
              </Typography.Text>
            </div>
          </div>
        }
        extra={
          <Space>
            <Input
              placeholder="용어, 영문, 동의어, 설명 검색"
              prefix={<SearchOutlined style={{ color: gray[400] }} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ width: 280, borderRadius: radius.md }}
            />
            <Tooltip title="새로고침">
              <Button icon={<ReloadOutlined />} onClick={fetchData} style={{ borderRadius: radius.md }} />
            </Tooltip>
            {canWrite && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
                style={{ borderRadius: radius.md, backgroundColor: brand.primary, borderColor: brand.primary }}>
                용어 등록
              </Button>
            )}
          </Space>
        }
      >
        {/* ── 필터바 ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          padding: '8px 12px', marginBottom: 14, background: gray[50],
          border: `1px solid ${gray[200]}`, borderRadius: 8,
        }}>
          <FilterOutlined style={{ color: gray[400] }} />
          <Typography.Text style={{ fontSize: 12, color: gray[500], fontWeight: 600 }}>분류</Typography.Text>
          <Tag
            onClick={() => setCategory('')}
            style={{
              cursor: 'pointer', borderRadius: 12, fontWeight: 600,
              color: !category ? '#fff' : gray[500],
              background: !category ? brand.primary : '#fff',
              borderColor: !category ? brand.primary : gray[200],
            }}
          >
            전체
          </Tag>
          {categories.map(c => (
            <Tag
              key={c}
              onClick={() => setCategory(category === c ? '' : c)}
              style={{
                cursor: 'pointer', borderRadius: 12, fontWeight: 600,
                color: category === c ? '#fff' : catColor(c),
                background: category === c ? catColor(c) : catColor(c) + '12',
                borderColor: catColor(c) + '55',
              }}
            >
              {c}
            </Tag>
          ))}
          <div style={{ width: 1, height: 16, background: gray[200], margin: '0 4px' }} />
          <Tag
            icon={<WarningOutlined />}
            onClick={() => setReviewOnly(v => !v)}
            style={{
              cursor: 'pointer', borderRadius: 12, fontWeight: 600,
              color: reviewOnly ? '#fff' : '#d97706',
              background: reviewOnly ? '#d97706' : '#fffbeb',
              borderColor: '#fcd34d',
            }}
          >
            뜻 확정 필요
          </Tag>
          {activeFilters > 0 && (
            <Button size="small" type="text" danger icon={<CloseCircleOutlined />}
              onClick={() => { setCategory(''); setReviewOnly(false); }}>
              필터 초기화 ({activeFilters})
            </Button>
          )}
          <div style={{ marginLeft: 'auto', fontSize: 12, color: gray[400] }}>{items.length}개 조회됨</div>
        </div>

        {/* ── 용어 목록 (분류별) ── 내부 스크롤 (Row 음수 gutter 가로 스크롤 방지로 overflowX hidden) */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: gray[400] }}>불러오는 중…</div>
        ) : items.length === 0 ? (
          <EmptyState title="등록된 용어가 없습니다" size="sm" />
        ) : (
          grouped.map(([cat, list]) => (
            <div key={cat} style={{ marginBottom: 18 }}>
              {/* 분류 헤더 */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
                padding: '5px 10px', background: catColor(cat) + '10',
                borderLeft: `3px solid ${catColor(cat)}`, borderRadius: 4,
              }}>
                <span style={{ fontWeight: 700, fontSize: 12, color: catColor(cat) }}>{cat}</span>
                <span style={{ fontSize: 11, color: gray[400] }}>{list.length}개</span>
              </div>

              <Row gutter={[10, 10]}>
                {list.map(t => (
                  <Col xs={24} lg={12} xxl={8} key={t.id}>
                    <div style={{
                      border: `1px solid ${gray[200]}`, borderRadius: 8, padding: '10px 12px',
                      height: '100%', background: '#fff',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                        <Typography.Text strong style={{ fontSize: 14, color: gray[800] }}>{t.term}</Typography.Text>
                        {t.term_en && (
                          <Typography.Text style={{ fontSize: 11, color: gray[400] }}>{t.term_en}</Typography.Text>
                        )}
                        {t.needs_review && (
                          <Tooltip title="뜻이 확정되지 않아 실무 확인이 필요한 항목입니다">
                            <Tag color="warning" style={{ fontSize: 10, marginRight: 0 }}>확인필요</Tag>
                          </Tooltip>
                        )}
                        <div style={{ flex: 1 }} />
                        {canWrite && (
                          <Tooltip title="수정">
                            <Button type="text" size="small" icon={<EditOutlined />}
                              style={{ color: brand.primary }} onClick={() => openEdit(t)} />
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Popconfirm title="삭제하시겠습니까?" okText="삭제" cancelText="취소"
                            okButtonProps={{ danger: true }} onConfirm={() => handleDelete(t.id)}>
                            <Tooltip title="삭제">
                              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                            </Tooltip>
                          </Popconfirm>
                        )}
                      </div>

                      <Typography.Paragraph style={{ fontSize: 12.5, margin: '6px 0 0', color: gray[700] }}>
                        {t.definition || '-'}
                      </Typography.Paragraph>

                      {t.detail && (
                        <Typography.Paragraph
                          type="secondary"
                          style={{ fontSize: 11.5, margin: '4px 0 0', whiteSpace: 'pre-line' }}
                          ellipsis={{ rows: 4, expandable: true, symbol: '더보기' }}
                        >
                          {t.detail}
                        </Typography.Paragraph>
                      )}

                      {(t.aliases || t.screens) && (
                        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                          {t.aliases && t.aliases.split(',').map(a => a.trim()).filter(Boolean).map(a => (
                            <Tag key={a} style={{ fontSize: 10, marginRight: 0, color: gray[500] }}>{a}</Tag>
                          ))}
                          {t.screens && (
                            <Tooltip title={`노출 화면: ${t.screens}`}>
                              <Tag style={{
                                fontSize: 10, marginRight: 0, cursor: 'default',
                                color: '#0ea5e9', background: '#f0f9ff', borderColor: '#bae6fd',
                              }}>
                                {t.screens.split(',').length}개 화면
                              </Tag>
                            </Tooltip>
                          )}
                        </div>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          ))
        )}
        </div>
      </Card>
      </div>

      {/* ── 등록 / 수정 ── */}
      <Modal
        title={
          <Space>
            {editing ? <EditOutlined style={{ color: brand.primary }} /> : <PlusOutlined style={{ color: brand.primary }} />}
            <span>{editing ? '용어 수정' : '용어 등록'}</span>
          </Space>
        }
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        onOk={submitForm}
        okText="저장"
        cancelText="취소"
        confirmLoading={saving}
        width={720}
        destroyOnClose
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto', paddingTop: 8 } }}
      >
        <Form form={form} layout="vertical" size="small">
          <Row gutter={10}>
            <Col span={9}>
              <Form.Item name="term" label="용어" rules={[{ required: true, message: '용어를 입력하세요' }]} style={{ marginBottom: 10 }}>
                <Input placeholder="예: 약효" />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item name="term_en" label="영문 / 원어 · 약어 풀이" style={{ marginBottom: 10 }}>
                <Input placeholder="예: efficacy" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="category" label="분류" style={{ marginBottom: 10 }}>
                <Select allowClear showSearch placeholder="선택 또는 입력"
                  options={Object.keys(CATEGORY_COLORS).map(c => ({ label: c, value: c }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="definition" label="정의 (한 줄)" style={{ marginBottom: 10 }}>
            <Input.TextArea rows={2} placeholder="이 용어가 무엇인지 한 문장으로" />
          </Form.Item>
          <Form.Item name="detail" label="상세 설명 · 실무 맥락" style={{ marginBottom: 10 }}>
            <Input.TextArea rows={4} placeholder="판정 기준, 주의점, 관련 규정 등" />
          </Form.Item>
          <Row gutter={10}>
            <Col span={12}>
              <Form.Item name="aliases" label="동의어 · 표기변형"
                extra={<span style={{ fontSize: 10, color: gray[400] }}>콤마로 구분 · 검색에 함께 걸립니다</span>}
                style={{ marginBottom: 10 }}>
                <Input placeholder="예: 약효조사, efficacy" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="screens" label="노출 화면"
                extra={<span style={{ fontSize: 10, color: gray[400] }}>콤마로 구분</span>}
                style={{ marginBottom: 10 }}>
                <Input placeholder="예: 시험일정총괄표, 시험설계서 목록" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={10}>
            <Col span={8}>
              <Form.Item name="needs_review" label="뜻 확정 필요" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Switch checkedChildren="예" unCheckedChildren="아니오" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="sort_order" label="정렬순서" style={{ marginBottom: 0 }}>
                <InputNumber min={1} max={9999} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="is_active" label="사용" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Switch checkedChildren="사용" unCheckedChildren="숨김" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default GlossaryPage;
